import { format } from "date-fns";

/**
 * Extract the day from a given date.
 * @param {Date} date - date
 * @returns {string} day
 *
 * ## Example
 * ```typescript
 * leadingZeroDay('2022-04-01T13:00:00.000Z') // => 1
 * ```
 */
export const dayOfMonthFormat = (date: Date): string => format(date, "d");

/**
 * Extract the short month name from a given date.
 * @param {Date} date - date
 * @returns {string} month
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => Apr
 * ```
 */
export const shortMonthFormat = (date: Date): string => format(date, "MMM");

/**
 * Extract the month from a given date.
 * @param {Date} date - date
 * @returns {string} month
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => April
 * ```
 */
export const monthFormat = (date: Date): string => format(date, "MMMM");

/**
 * Format the given date to be in a short date format.
 * @param {Date} date - date
 * @returns {string} hortten date
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => Apr 12, 2022
 * ```
 */
export const shortDate = (date: Date): string => format(date, "PP");

/**
 * Format the given date to be in a long date format.
 * @param {Date} date - date
 * @returns {string} shortten date
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => April 12th, 2022
 * ```
 */
export const longDate = (date: Date): string => format(date, "PPP");

/**
 * Format the given date to be in a long localized date.
 * @param {Date} date - date
 * @returns {string} shortten date
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => 12/04/2022
 * ```
 */
export const longLocalizedDate = (date: Date): string => format(date, "P");

export function formatToFilenameDate(date: Date) {
  return format(date, "yyyy-MM-dd_HH-mm");
}

export function formatSecondsToTime(seconds: number) {
  // Format the Date object to get minutes and seconds
  const formattedTime = format(new Date(seconds * 1000), "m:ss");
  return formattedTime;
}

type LongPrettifiedDateArgs = {
  locale?: string;
};

export function longPrettifiedDate(
  date: Date,
  { locale = "en-US" }: LongPrettifiedDateArgs
) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "medium",
  }).format(date);
}
